import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box, Container, createTheme, Skeleton, Stack,
    Tab, Tabs, ThemeProvider, Typography
} from "@mui/material";
import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import CreatorCard from "./common/CreatorCard";
import CreatorStats from "./common/CreatorStats";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import paymentdashboardservice from "../../services/paymentdashboardservice";
import GetCookieByName from "../../hooks/GetCookie";
import PaymentLedgerTable from "./common/PaymentLedgerTable";
import CreatorReportTab from "./common/CreatorReportTab";
import CreatorConversationsTab from "./common/CreatorConversationsTab";
import CreatorWhatsAppTab from "./common/CreatorWhatsAppTab";
import CreatorEmailTab from "./common/CreatorEmailTab";
import CreatorRemarksTab from "./common/CreatorRemarksTab";
import CreatorPlanTab from "./common/CreatorPlanTab";
import helperFunctions from '../../hooks/HelperFunctions';

const theme = createTheme({
    typography: {
        fontFamily: ['Quicksand', 'Playfair Display'].join(','),
        fontSize: 16,
    },
});

const PAGE_BG = {
    minHeight: '100vh',
    background: 'linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)',
    backgroundAttachment: {
        xs:'scroll',
        md:'fixed'
    },
};

const PAGE_FADE = {
    "@keyframes pageFadeIn": {
        "0%":   { opacity: 0, transform: "translateY(12px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
    },
};

function LoadingSkeleton() {
    return (
        <Box sx={PAGE_BG}>
            <DashboardHeaderSection />
            <ThemeProvider theme={theme}>
                <Container sx={{ marginTop: 12, pl: { xs: 2, sm: 3, lg: '108px' }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
                    <Skeleton variant="rounded" height={106} sx={{ borderRadius: 4, mb: 3 }} />
                    <Skeleton variant="rounded" height={52} sx={{ borderRadius: '12px 12px 0 0' }} />
                    <Skeleton variant="rounded" height={380} sx={{ borderRadius: '0 0 12px 12px', mt: 0 }} />
                </Container>
            </ThemeProvider>
        </Box>
    );
}

// Appends a new message into the cached conversations payload, de-duping on the given
// message-id field so an optimistic send and its later socket echo (or the server's HTTP
// response) don't both land as separate bubbles. idField differs per channel: Instagram
// messages carry igMessageId, WhatsApp messages carry waMessageId.
function upsertMessage(oldData, conversationId, message, idField = 'igMessageId') {
    if (!oldData?.conversations) return oldData;
    return {
        ...oldData,
        conversations: oldData.conversations.map((conv) => {
            if (conv.conversationId !== conversationId) return conv;
            const alreadyExists = conv.messages.some((m) => m[idField] === message[idField]);
            if (alreadyExists) return conv;
            return {
                ...conv,
                lastMessageAt: message.sentAt,
                messages: [...conv.messages, message],
            };
        }),
    };
}

// WhatsApp delivery-status ticks (sent -> delivered -> read) arrive as a separate socket
// event from the message itself (whatsappWebhook.js's status handler emits
// 'whatsapp-message-status', not 'new-whatsapp-message') — nothing was listening for it, so
// ticks only ever updated on a manual refetch, never live.
function upsertMessageStatus(oldData, conversationId, waMessageId, status) {
    if (!oldData?.conversations) return oldData;
    return {
        ...oldData,
        conversations: oldData.conversations.map((conv) => {
            if (conv.conversationId !== conversationId) return conv;
            return {
                ...conv,
                messages: conv.messages.map((m) => (m.waMessageId === waMessageId ? { ...m, status } : m)),
            };
        }),
    };
}

function CreatorPaymentHistory() {
    const params = useParams();
    const creatorId = params.creatorId;
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const socketRef = useRef(null);
    const joinedRoomsRef = useRef(new Set());
    // activeTab is a stable key, not a raw index — tab positions shift depending on
    // whether Conversations exists, so a raw index clicked before that resolves would
    // point at the wrong tab once it does.
    const [activeTab, setActiveTab] = useState(
        searchParams.get('tab') === 'remarks' ? 'REMARKS'
            : searchParams.get('tab') === 'conversations' ? 'CONVERSATIONS'
            : 'COLLAB'
    );
    // Which channel is showing inside the Conversations tab — independent of activeTab
    // so switching Collab/Report/Remarks doesn't reset which channel was open.
    const [conversationChannel, setConversationChannel] = useState('INSTAGRAM');

    const { data: historicalData, isLoading, error } = useQuery({
        queryKey: ['creatorpaymenthistory', creatorId],
        queryFn: () => paymentdashboardservice.getCreatorPaymentHistory(creatorId),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data: reportData, isLoading: reportLoading } = useQuery({
        queryKey: ['creatorreporthistory', creatorId],
        queryFn: () => paymentdashboardservice.getCreatorReportHistory(creatorId),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data: conversationsData, isLoading: convLoading } = useQuery({
        queryKey: ['creatorconversations', creatorId],
        queryFn: () => paymentdashboardservice.getCreatorConversations(creatorId),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data: whatsappData, isLoading: whatsappLoading } = useQuery({
        queryKey: ['creatorwhatsapp', creatorId],
        queryFn: () => paymentdashboardservice.getCreatorWhatsApp(creatorId),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data: emailData, isLoading: emailLoading } = useQuery({
        queryKey: ['creatoremail', creatorId],
        queryFn: () => paymentdashboardservice.getCreatorEmail(creatorId),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data: planData, isLoading: planLoading, error: planError } = useQuery({
        queryKey: ['creatorplans', creatorId],
        queryFn: () => paymentdashboardservice.getCreatorPlans(creatorId),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const hasInstagramConversations = conversationsData?.hasConversations === true;
    const hasWhatsAppConversations = whatsappData?.hasConversations === true;
    const hasEmailConversations = emailData?.hasConversations === true;
    const hasConversations = hasInstagramConversations || hasWhatsAppConversations || hasEmailConversations;
    // Tab order/keys, in render order — always re-derived, so a key resolves to the
    // right index however Conversations' presence has shifted things around it.
    const TAB_KEYS = ['COLLAB', 'REPORT', ...(hasConversations ? ['CONVERSATIONS'] : []), 'REMARKS', 'PLAN'];
    const remarksTabIndex = TAB_KEYS.indexOf('REMARKS');
    const planTabIndex = TAB_KEYS.indexOf('PLAN');
    const safeActiveTab = TAB_KEYS.includes(activeTab) ? TAB_KEYS.indexOf(activeTab) : 0;

    const isConversationsTabActive = hasConversations && safeActiveTab === 2;

    // Only hold a live connection while the Conversations tab is actually open —
    // no point keeping a socket around while staff are on Report/Remarks/Plan.
    // One socket serves both channels: joins every Instagram conversation room plus
    // every WhatsApp customerWaId room, and routes each event into its own query cache.
    //
    // Deliberately does NOT depend on conversationsData/whatsappData — those are exactly
    // what the message handlers below write into via setQueryData, so depending on them
    // here made this effect tear down and recreate the socket after every single incoming
    // message (new data -> effect re-runs -> old socket disconnected -> new one connects,
    // rejoining rooms from scratch). Whichever message arrived while that reconnect was in
    // flight was silently dropped — this is what made messages after the first one not show
    // up live. Room joins are handled by the separate effect below instead, which updates
    // incrementally on the existing socket rather than recreating it.
    useEffect(() => {
        if (!isConversationsTabActive) return undefined;

        let authToken;
        try {
            authToken = JSON.parse(GetCookieByName("auth"))["token"];
        } catch (e) {
            return undefined;
        }

        const socket = io(new URL(process.env.REACT_APP_API_URL).origin, {
            auth: { token: authToken },
        });
        socketRef.current = socket;
        joinedRoomsRef.current = new Set();

        socket.on("new-message", ({ conversationId, message }) => {
            queryClient.setQueryData(['creatorconversations', creatorId], (old) =>
                upsertMessage(old, conversationId, message, 'igMessageId')
            );
        });

        socket.on("new-whatsapp-message", ({ conversationId, message }) => {
            queryClient.setQueryData(['creatorwhatsapp', creatorId], (old) =>
                upsertMessage(old, conversationId, message, 'waMessageId')
            );
        });

        socket.on("whatsapp-message-status", ({ conversationId, waMessageId, status }) => {
            queryClient.setQueryData(['creatorwhatsapp', creatorId], (old) =>
                upsertMessageStatus(old, conversationId, waMessageId, status)
            );
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, [isConversationsTabActive, creatorId, queryClient]);

    // Joins any conversation rooms not already joined on the current socket — runs whenever
    // the conversation lists change (initial load, or a new thread appearing), without
    // touching the socket connection itself.
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket) return;

        const igConvs = conversationsData?.conversations || [];
        const waConvs = whatsappData?.conversations || [];
        const toJoin = [...igConvs, ...waConvs]
            .map((c) => c.conversationId)
            .filter((id) => id && !joinedRoomsRef.current.has(id));
        if (!toJoin.length) return;

        const doJoin = () => {
            toJoin.forEach((id) => {
                socket.emit("join", id);
                joinedRoomsRef.current.add(id);
            });
        };
        if (socket.connected) doJoin();
        else socket.once("connect", doJoin);
    }, [conversationsData, whatsappData]);

    const handleSendMessage = async (conversationId, text) => {
        const res = await paymentdashboardservice.sendCreatorMessage(conversationId, text);
        if (!res?.success) throw new Error(res?.message || "Failed to send message");
        queryClient.setQueryData(['creatorconversations', creatorId], (old) =>
            upsertMessage(old, conversationId, res.message, 'igMessageId')
        );
    };

    const handleSendWhatsAppMessage = async (conversationId, text) => {
        const res = await paymentdashboardservice.sendCreatorWhatsAppMessage(conversationId, text);
        if (!res?.success) throw new Error(res?.message || "Failed to send message");
        queryClient.setQueryData(['creatorwhatsapp', creatorId], (old) =>
            upsertMessage(old, conversationId, res.message, 'waMessageId')
        );
    };

    if (isLoading) return <LoadingSkeleton />;

    if (error || !historicalData || historicalData?.['success'] === false) {
        return (
            <Box sx={PAGE_BG}>
                <DashboardHeaderSection />
                <ThemeProvider theme={theme}>
                    <Container sx={{ marginTop: 12, pl: { xs: 2, sm: 3, lg: '108px' }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
                        <Box sx={{ textAlign: 'center', py: 10, borderRadius: 4, background: '#fff', border: '1px solid rgba(255,82,0,0.1)' }}>
                            <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 700, color: '#6B7280', fontSize: 15 }}>
                                Could not load creator data. Please try again.
                            </Typography>
                        </Box>
                    </Container>
                </ThemeProvider>
            </Box>
        );
    }

    const {
        creator,
        paymentTillDate,
        highestCampaignPayment,
        totalCampaigns,
        firstRelationshipDate,
        latestRelationshipDate,
        campaignTable,
        latestCampaignPayment,
    } = historicalData['history'];

    let relationshipDuration = '';
    if (firstRelationshipDate && latestRelationshipDate) {
        relationshipDuration = helperFunctions.durationBetweenDates(firstRelationshipDate, latestRelationshipDate);
    }

    return (
        <Box sx={PAGE_BG}>
            <DashboardHeaderSection />
            <ThemeProvider theme={theme}>
                <Container
                    sx={{ marginTop: 12, pb: 6, pl: { xs: 2, sm: 3, lg: '108px' }, pr: { xs: 2, sm: 3 }, ...PAGE_FADE, animation: "pageFadeIn 0.4s ease both" }}
                    maxWidth="xl"
                >
                    <CreatorCard creator={creator} relationship={relationshipDuration} />

                    {/* Tab bar */}
                    <Box
                        sx={{
                            mt: 3,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(255,248,245,0.94) 100%)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '12px 12px 0 0',
                            border: '1px solid rgba(255,82,0,0.10)',
                            borderBottom: '1px solid rgba(255,82,0,0.07)',
                            boxShadow: '0 2px 12px rgba(255,82,0,0.04)',
                            px: 2,
                        }}
                    >
                        <Tabs
                            value={safeActiveTab}
                            onChange={(_, val) => setActiveTab(TAB_KEYS[val])}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            TabIndicatorProps={{
                                style: {
                                    backgroundColor: '#FF5200',
                                    height: 3,
                                    borderRadius: '3px 3px 0 0',
                                },
                            }}
                            sx={{
                                '& .MuiTab-root': {
                                    fontFamily: 'Quicksand',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    textTransform: 'none',
                                    color: '#6B7280',
                                    minWidth: {
                                        xs:120,
                                        sm:150,
                                        md:180
                                    },
                                    py: 1.75,
                                    transition: 'color 0.2s ease',
                                },
                                '& .Mui-selected': {
                                    color: '#FF5200 !important',
                                },
                            }}
                        >
                            <Tab label="Past Collaborations" />
                            <Tab label="Report" />
                            {hasConversations && <Tab label="Conversations" />}
                            <Tab label="Remarks" />
                            <Tab label="Plan" />
                        </Tabs>
                    </Box>

                    {/* Tab content card */}
                    <Box
                        sx={{
                            background: 'rgba(255,255,255,0.85)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '0 0 12px 12px',
                            border: '1px solid rgba(255,82,0,0.08)',
                            borderTop: 'none',
                            p: { xs: '14px 12px 28px', sm: '20px 24px 36px' },
                            boxShadow: '0 4px 24px rgba(255,82,0,0.04)',
                            minHeight: 420,
                        }}
                    >
                        {safeActiveTab === 0 && (
                            <Box>
                                {paymentTillDate > 0 && (
                                    <CreatorStats
                                        totalPaid={paymentTillDate}
                                        totalCampaigns={totalCampaigns}
                                        maximumPayment={highestCampaignPayment}
                                        recentPayment={latestCampaignPayment}
                                    />
                                )}
                                {campaignTable?.length > 0 && (
                                    <PaymentLedgerTable content={campaignTable} />
                                )}
                                {(!paymentTillDate || paymentTillDate === 0) && (!campaignTable || campaignTable.length === 0) && (
                                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8, gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 64, height: 64, borderRadius: '50%',
                                                background: 'linear-gradient(135deg, rgba(255,82,0,0.07) 0%, rgba(255,140,0,0.04) 100%)',
                                                border: '2px dashed rgba(255,82,0,0.20)',
                                                display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', fontSize: 26,
                                            }}
                                        >
                                            💸
                                        </Box>
                                        <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 800, color: '#1C1C2E', fontSize: 15 }}>
                                            No payments recorded yet
                                        </Typography>
                                        <Typography sx={{ fontFamily: 'Quicksand', color: '#6B7280', fontSize: 13, maxWidth: 280, textAlign: 'center' }}>
                                            Payments with this creator will show up here once they are added.
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        )}

                        {safeActiveTab === 1 && (
                            <CreatorReportTab reportData={reportData} isLoading={reportLoading} />
                        )}

                        {safeActiveTab === 2 && hasConversations && (
                            <Box>
                                <Tabs
                                    value={conversationChannel}
                                    onChange={(_, val) => setConversationChannel(val)}
                                    TabIndicatorProps={{
                                        style: {
                                            backgroundColor: conversationChannel === 'WHATSAPP' ? '#25D366' : conversationChannel === 'EMAIL' ? '#1A73E8' : '#FF5200',
                                            height: 2.5,
                                            borderRadius: '3px 3px 0 0',
                                        },
                                    }}
                                    sx={{
                                        minHeight: 36,
                                        mb: 2,
                                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                                        '& .MuiTab-root': {
                                            fontFamily: 'Quicksand',
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            textTransform: 'none',
                                            color: '#6B7280',
                                            minHeight: 36,
                                            py: 1,
                                        },
                                        '& .Mui-selected': {
                                            color: (conversationChannel === 'WHATSAPP' ? '#25D366' : conversationChannel === 'EMAIL' ? '#1A73E8' : '#FF5200') + ' !important',
                                        },
                                    }}
                                >
                                    <Tab label="Instagram" value="INSTAGRAM" />
                                    <Tab label="WhatsApp" value="WHATSAPP" />
                                    <Tab label="Email" value="EMAIL" />
                                </Tabs>

                                {conversationChannel === 'INSTAGRAM' && (
                                    <CreatorConversationsTab
                                        conversations={conversationsData?.conversations || []}
                                        isLoading={convLoading}
                                        onSendMessage={handleSendMessage}
                                    />
                                )}

                                {conversationChannel === 'WHATSAPP' && (
                                    <CreatorWhatsAppTab
                                        conversations={whatsappData?.conversations || []}
                                        isLoading={whatsappLoading}
                                        onSendMessage={handleSendWhatsAppMessage}
                                    />
                                )}

                                {conversationChannel === 'EMAIL' && (
                                    <CreatorEmailTab
                                        conversations={emailData?.conversations || []}
                                        isLoading={emailLoading}
                                        creatorId={creatorId}
                                    />
                                )}
                            </Box>
                        )}

                        {safeActiveTab === remarksTabIndex && (
                            <CreatorRemarksTab creatorId={creatorId} />
                        )}

                        {safeActiveTab === planTabIndex && (
                            <CreatorPlanTab planData={planData} isLoading={planLoading} error={planError} />
                        )}
                    </Box>
                </Container>
            </ThemeProvider>
        </Box>
    );
}

export default CreatorPaymentHistory;
